// Dashboard alerts: what can be watched, and what an alert looks like.
import type { Stack, StackType } from "./stack.js";

/**
 * What a monitor watches.
 * - `stock.below`: a watched stack (or every rule, optionally of one type) under a threshold, or under its rule's min.
 * - `craft.failed`: a craft that failed (all stacks or one).
 * - `link.down`: the game link is down.
 * - `power.low`: AE stored power under a percentage of the maximum.
 * - `game.error`: the Butler logged an error.
 * - `production.stopped`: a watched stack has not gone up for `threshold` minutes (one stack, or every
 *   resource without a rule that went up in the last day, such as an auto-prod line).
 * - `storage.full`: the storage cells have `threshold` percent of their bytes or of their types used.
 */
export type AlertKind = "stock.below" | "craft.failed" | "link.down" | "power.low" | "game.error" | "production.stopped" | "storage.full";

export const ALERT_KINDS: readonly AlertKind[] = ["stock.below", "craft.failed", "production.stopped", "storage.full", "link.down", "power.low", "game.error"];

export const ALERT_KIND_LABEL: Record<AlertKind, string> = {
  "stock.below": "Stock below",
  "craft.failed": "Craft failed",
  "link.down": "Game link down",
  "power.low": "AE power low",
  "game.error": "Butler error",
  "production.stopped": "Production stopped",
  "storage.full": "Storage nearly full",
};

/** A condition the dashboard watches, chosen on the Alerts page. */
export interface AlertMonitor {
  id: number;
  kind: AlertKind;
  enabled: boolean;
  /** The one stack this monitor watches (stock.below, craft.failed, production.stopped). Null means every one. */
  stack: Stack | null;
  key: string | null;
  /** With no stack: only stacks of this type. Null means every type. */
  stackType: StackType | null;
  /** stock.below: amount (null means the rule's min). power.low, storage.full: percent. production.stopped: minutes without a rise. Unused otherwise. */
  threshold: number | null;
  /** Minutes the condition must hold before it alerts (stock.below, link.down, power.low, storage.full). */
  delayMinutes: number;
  createdAt: number;
  updatedAt: number;
}

export type AlertMonitorInput = Partial<Pick<AlertMonitor, "kind" | "enabled" | "stack" | "stackType" | "threshold" | "delayMinutes">>;

/** A raised alert. It stays on the dashboard until someone dismisses it. */
export interface Alert {
  id: number;
  monitorId: number | null;
  kind: AlertKind;
  /** What it is about: a stack key, "link", "power" or "game". */
  subject: string;
  level: "warn" | "error";
  title: string;
  msg: string;
  /** Times it fired while not dismissed. */
  count: number;
  firstAt: number;
  lastAt: number;
  /** False once the condition cleared (the alert still waits to be dismissed). */
  active: boolean;
  dismissedAt: number | null;
}
